import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, X as XIcon, ShoppingCart, Receipt, ChevronRight, Cpu, Battery, Monitor, Snowflake, Home } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import DeviceCard from "@/components/DeviceCard";
import OutboundModal from "@/components/OutboundModal";
import { FpsBadge, PtaBadge, ConditionBadge, CoolingBadge } from "@/components/Badges";
import { fetchDevice } from "@/lib/api";
import { GAMES } from "@/data/static";

const pkr = (n) => `PKR ${n.toLocaleString("en-PK")}`;

export default function DeviceDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    fetchDevice(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Center>Device not found. <Link to="/fps-finder" className="text-neon underline">Browse all phones</Link></Center>;
  if (!data) return <Center>Loading…</Center>;

  const d = data.device;
  const related = data.related || [];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: d.name,
      image: d.image,
      brand: { "@type": "Brand", name: d.brand },
      description: `${d.name} - ${d.chipset}. Real per-game FPS and live Pakistan price in PKR.`,
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "PKR",
        lowPrice: d.price_min,
        highPrice: d.price_max,
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://gamingkits.online/" },
        { "@type": "ListItem", position: 2, name: "FPS Finder", item: "https://gamingkits.online/fps-finder" },
        { "@type": "ListItem", position: 3, name: d.name, item: `https://gamingkits.online/device/${d.slug}` },
      ],
    },
  ];

  return (
    <>
      <Seo title={`${d.name} - Gaming Performance & Price`} description={`${d.name} gaming review for Pakistan: ${d.chipset}, real per-game FPS, thermals, PTA status and live price in PKR.`} path={`/device/${d.slug}`} jsonLd={jsonLd} />

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <nav className="mb-6 flex items-center gap-1.5 text-xs text-slate-500">
          <Link to="/" className="hover:text-white"><Home className="h-3.5 w-3.5" /></Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/fps-finder" className="hover:text-white">FPS Finder</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-300">{d.name}</span>
        </nav>

        <div className="grid gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-border bg-slateSurface">
            <img src={d.image} alt={`${d.name} gaming smartphone`} className="aspect-[4/3] w-full object-contain p-6" />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <FpsBadge fps={d.fps_tier} />
              <PtaBadge status={d.pta_status} />
              <CoolingBadge rating={d.cooling_rating} />
            </div>
            <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-white sm:text-4xl">{d.name}</h1>
            <p className="mt-2 text-sm text-slate-400">{d.brand} · {d.chipset} · {d.ram_storage}</p>

            <div className="mt-5 rounded-xl border border-neon/30 bg-neon/5 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-400">Live price in Pakistan (PKR)</div>
              <div className="mt-1 font-display text-2xl font-black text-neon">{d.price_min === d.price_max ? pkr(d.price_min) : `${pkr(d.price_min)} - ${pkr(d.price_max)}`}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(d.conditions || []).map((c) => <ConditionBadge key={c} label={c} />)}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <button data-testid="detail-buy-btn" onClick={() => setModal("marketplace")} className="col-span-1 flex items-center justify-center gap-2 rounded-lg bg-neon px-4 py-3 text-sm font-heading font-bold text-charcoal transition-all hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] sm:col-span-2">
                <ShoppingCart className="h-4 w-4" /> Buy on Partner Site
              </button>
              <button data-testid="detail-pta-btn" onClick={() => setModal("pta")} className="flex items-center justify-center gap-2 rounded-lg border border-cyber/40 bg-cyber/10 px-4 py-3 text-sm font-heading font-bold text-cyber transition-all hover:bg-cyber/20">
                <Receipt className="h-4 w-4" /> PTA Tax
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Outbound links are external & unaffiliated. Verify listings and prices independently.</p>
          </div>
        </div>

        {/* SPECS */}
        <section className="mt-10">
          <H2>Specifications</H2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Spec icon={Cpu} label="Chipset" value={d.chipset} />
            <Spec icon={Monitor} label="Display" value={d.display} />
            <Spec icon={Battery} label="Battery" value={d.battery} />
            <Spec icon={Snowflake} label="Cooling" value={`${d.cooling_rating} - ${d.cooling_notes}`} />
          </div>
        </section>

        {/* GAMING BY TITLE */}
        <section className="mt-10">
          <H2>Reported Gaming Performance by Title</H2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {GAMES.map((g) => (
              <div key={g.key} className="rounded-xl border border-border bg-slateSurface p-4">
                <div className="font-heading text-sm font-bold text-white">{g.label}</div>
                <p className="mt-1 text-sm text-slate-300">{(d.games || {})[g.key] || "Data pending"}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InfoLine label="Thermal behaviour" value={d.thermal_label} />
            <InfoLine label="Gyro / latency" value={d.gyro_label} />
          </div>
        </section>

        {/* PROS CONS */}
        <section className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-neon/30 bg-slateSurface p-5">
            <h3 className="font-heading text-sm font-bold text-neon">Key Pros</h3>
            <ul className="mt-3 space-y-2">
              {(d.pros || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" /> {p}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-dangerr/30 bg-slateSurface p-5">
            <h3 className="font-heading text-sm font-bold text-dangerr">Key Cons</h3>
            <ul className="mt-3 space-y-2">
              {(d.cons || []).map((c) => (
                <li key={c} className="flex gap-2 text-sm text-slate-300"><XIcon className="mt-0.5 h-4 w-4 shrink-0 text-dangerr" /> {c}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* USED MARKET */}
        <section className="mt-10">
          <H2>Used-Market Notes for Pakistan</H2>
          <div className="rounded-xl border border-border bg-slateSurface p-5 text-sm leading-relaxed text-slate-300">{d.used_market_notes}</div>
        </section>

        {/* CHECKLIST */}
        <section className="mt-8">
          <H2>Condition Buying Checklist</H2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(d.buying_checklist || []).map((c) => (
              <div key={c} className="flex gap-2 rounded-lg border border-border bg-graphite p-3 text-sm text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyber" /> {c}
              </div>
            ))}
          </div>
        </section>

        <div className="mt-8"><Disclaimer /></div>

        {related.length > 0 && (
          <section className="mt-10">
            <H2>Related Devices</H2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => <DeviceCard key={r.slug} device={r} />)}
            </div>
          </section>
        )}

        <p className="mt-8 text-xs text-slate-500">Last updated: {d.updated_date} · Prices vary by city and seller condition · Performance may vary by software version and ambient temperature.</p>
      </div>

      <OutboundModal open={!!modal} mode={modal} deviceName={d.name} deviceBrand={d.brand} onClose={() => setModal(null)} />
    </>
  );
}

const Center = ({ children }) => <div className="flex min-h-[50vh] items-center justify-center px-4 text-center text-slate-400">{children}</div>;
const H2 = ({ children }) => <h2 className="mb-4 font-heading text-xl font-bold text-white sm:text-2xl">{children}</h2>;
const Spec = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-border bg-slateSurface p-4">
    <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="h-4 w-4 text-neon" /> {label}</div>
    <div className="mt-1.5 text-sm text-slate-200">{value}</div>
  </div>
);
const InfoLine = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-graphite p-4">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="mt-1 text-sm font-medium text-cyber">{value}</div>
  </div>
);
